'use client';
import React from 'react';
import { usePermissions } from './hooks/usePermissions';
import { PermissionPrompt } from './permissionPrompt';
import { Permission } from '@benzinga/permission-manager';
import { HijackComponent } from './hijackComponent';
import { Slug } from './entity';

export type PermissionType =
  | {
      permission: Permission;
      permissionsAnd?: undefined;
      permissionsOr?: undefined;
    }
  | {
      permission?: undefined;
      permissionsAnd: Permission[];
      permissionsOr?: undefined;
    }
  | {
      permission?: undefined;
      permissionsAnd?: undefined;
      permissionsOr: Permission[];
    }
  | {
      permission?: undefined;
      permissionsAnd?: undefined;
      permissionsOr?: Permission[];
    };

export type PermissionArgs = {
  disableTrial?: boolean;
  isFeatureUpdate?: boolean;
  showUpsell?: boolean;
  slug?: Slug;
  trackingInfoAppName?: string;
  trackingInfoMessage?: string;
  resourceName?: string;
};

interface ComponentProps {
  children: React.ReactNode | ((access: boolean) => React.ReactNode);
  doNotDisplayPrompt?: boolean;
  doNotStopPropagation?: boolean;
  displayPromptOnDenied?: boolean;
  onPurchase?: () => void;
  onDisplayPrompt?: (props: PermissionArgs & PermissionType) => void;
  onPurchasePromptClose?: () => void;
}

type Props = ComponentProps & PermissionArgs & PermissionType & { supplementalOrAccess?: boolean };

export const PermissionedComponent: React.FC<Props> = props => {
  const { permissions, type } = React.useMemo<{ permissions: Permission[]; type: 'and' | 'or' }>(() => {
    if (props.permission) {
      return {
        permissions: [props.permission],
        type: 'and',
      };
    } else if (props.permissionsAnd) {
      return {
        permissions: props.permissionsAnd,
        type: 'and',
      };
    } else if (props.permissionsOr) {
      return {
        permissions: props.permissionsOr,
        type: 'or',
      };
    } else {
      return {
        permissions: [],
        type: 'or',
      };
    }
  }, [props.permission, props.permissionsAnd, props.permissionsOr]);

  const hasAccess = usePermissions(permissions, type) || (props.supplementalOrAccess ?? false);

  const [isDisplayingPrompt, setIsDisplayingPrompt] = React.useState(props.displayPromptOnDenied ? !hasAccess : false);

  const onDisplay = React.useCallback((): void => {
    const onDisplayPrompt = props.onDisplayPrompt;
    onDisplayPrompt?.({
      disableTrial: props.disableTrial,
      isFeatureUpdate: props.isFeatureUpdate,
      permission: props.permission,
      permissionsAnd: props.permissionsAnd,
      permissionsOr: props.permissionsOr,
      slug: props?.slug,
      trackingInfoAppName: props.trackingInfoAppName,
      trackingInfoMessage: props.trackingInfoMessage,
    } as PermissionArgs & PermissionType);
    setIsDisplayingPrompt(!props.doNotDisplayPrompt);
  }, [
    props.disableTrial,
    props.doNotDisplayPrompt,
    props.isFeatureUpdate,
    props.onDisplayPrompt,
    props.permission,
    props.permissionsAnd,
    props.permissionsOr,
    props?.slug,
    props.trackingInfoAppName,
    props.trackingInfoMessage,
  ]);

  const content = React.useMemo(() => {
    const childrenFunc = props.children;
    return typeof childrenFunc === 'function' ? childrenFunc(hasAccess) : childrenFunc;
  }, [hasAccess, props.children]);

  const promptPermission = React.useMemo((): Permission[] | Permission | undefined => {
    if (props.permission) {
      return props.permission;
    }
    if (props.permissionsAnd?.length) {
      return props.permissionsAnd;
    }
    if (props.permissionsOr?.length) {
      return props.permissionsOr;
    }
    return undefined;
  }, [props.permission, props.permissionsAnd, props.permissionsOr]);

  const promptPermissionType = props.permissionsOr ? 'or' : 'and';

  const onClose = React.useCallback(() => {
    setIsDisplayingPrompt(false);
    const onPurchasePromptClose = props.onPurchasePromptClose;
    onPurchasePromptClose?.();
  }, [props.onPurchasePromptClose]);

  return (
    <>
      {isDisplayingPrompt && (
        <PermissionPrompt
          disableTrial={props.disableTrial}
          isFeatureUpdate={props.isFeatureUpdate}
          onClose={onClose}
          permission={promptPermission}
          permissionType={promptPermissionType}
          resourceName={props.resourceName}
          showUpsell={props.showUpsell ?? promptPermission === undefined}
          slug={props?.slug}
          trackingInfoAppName={props.trackingInfoAppName}
          trackingInfoMessage={props.trackingInfoMessage}
        />
      )}
      <HijackComponent doNotStopPropagation={props.doNotStopPropagation} hijack={!hasAccess} onHijackClick={onDisplay}>
        {content}
      </HijackComponent>
    </>
  );
};
